import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  FileText,
  Calculator,
  DollarSign,
  Info,
  ExternalLink,
  BookOpen,
  AlertCircle,
  BarChart3,
  Clock,
  Target,
  Database,
  RefreshCw,
  MessageSquare,
  Send
} from 'lucide-react';

const CBAComprehensiveDisplay = ({ cbaData, onRegenerateWithContext }) => {
  const [expandedSections, setExpandedSections] = useState({
    executiveSummary: true,
    costs: false,
    benefits: false,
    qualitative: false,
    methodology: false
  });
  
  const [expandedItems, setExpandedItems] = useState({});
  const [showContextInput, setShowContextInput] = useState(false);
  const [userContext, setUserContext] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return amount;
  };

  // Group items by category
  const groupByCategory = (items) => {
    const grouped = {};
    items?.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    return grouped;
  };

  const costsByCategory = groupByCategory(cbaData.costs);
  const benefitsByCategory = groupByCategory(cbaData.benefits);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
      borderRadius: '16px',
      padding: '32px',
      minHeight: '600px'
    }}>
      {/* Header Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '32px',
        maxWidth: '100%'
      }}>
        {/* Total Costs Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            borderTop: '3px solid #dc2626',
            minWidth: '140px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <TrendingDown style={{ width: '16px', height: '16px', color: '#dc2626' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>TOTAL COSTS</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626', margin: 0 }}>
            {formatCurrency(cbaData.summary?.total_costs || 0)}
          </p>
          <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>per year</p>
        </motion.div>

        {/* Total Benefits Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            borderTop: '3px solid #16a34a',
            minWidth: '140px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <TrendingUp style={{ width: '16px', height: '16px', color: '#16a34a' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>TOTAL BENEFITS</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a', margin: 0 }}>
            {formatCurrency(cbaData.summary?.total_benefits || 0)}
          </p>
          <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>per year</p>
        </motion.div>

        {/* Net Benefit Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            borderTop: `3px solid ${cbaData.summary?.net_benefit >= 0 ? '#2563eb' : '#ef4444'}`,
            minWidth: '140px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Target style={{ width: '16px', height: '16px', color: cbaData.summary?.net_benefit >= 0 ? '#2563eb' : '#ef4444' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>NET BENEFIT</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: cbaData.summary?.net_benefit >= 0 ? '#2563eb' : '#ef4444', margin: 0 }}>
            {formatCurrency(cbaData.summary?.net_benefit || 0)}
          </p>
          <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>per year</p>
        </motion.div>

        {/* B/C Ratio Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            borderTop: '3px solid #8b5cf6',
            minWidth: '140px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <BarChart3 style={{ width: '16px', height: '16px', color: '#8b5cf6' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>B/C RATIO</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: '#8b5cf6', margin: 0 }}>
            {cbaData.summary?.benefit_cost_ratio || '0.00'}
          </p>
          <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>ratio</p>
        </motion.div>

        {/* Payback Period Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            borderTop: '3px solid #f59e0b',
            minWidth: '140px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Clock style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>PAYBACK</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b', margin: 0 }}>
            {cbaData.summary?.payback_period || 'N/A'}
          </p>
          <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>years</p>
        </motion.div>
      </div>

      {/* Regenerate with Context Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.32 }}
        style={{
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid #e5e7eb'
        }}
      >
        {!showContextInput ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <RefreshCw style={{ width: '16px', height: '16px' }} />
                Refine This Analysis
              </h3>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                Add specific context or requirements to regenerate with more targeted analysis
              </p>
            </div>
            <button
              onClick={() => setShowContextInput(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <MessageSquare style={{ width: '14px', height: '14px' }} />
              Add Context
            </button>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <MessageSquare style={{ width: '16px', height: '16px' }} />
                Provide Additional Context
              </h3>
              <button
                onClick={() => {
                  setShowContextInput(false);
                  setUserContext('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>
            
            <textarea
              value={userContext}
              onChange={(e) => setUserContext(e.target.value)}
              placeholder="Example: Focus on small businesses with fewer than 50 employees, or analyze impacts on rural Virginia communities, or include environmental compliance costs..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '12px',
                background: 'white'
              }}
            />
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowContextInput(false);
                  setUserContext('');
                }}
                style={{
                  background: 'white',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  console.log('%c🔄 [CBAComprehensiveDisplay] Regenerate button clicked', 'color: #F59E0B; font-weight: bold');
                  console.log('%c📝 [CBAComprehensiveDisplay] User context:', 'color: #3B82F6', userContext);
                  console.log('%c🔧 [CBAComprehensiveDisplay] Has callback:', 'color: #10B981', !!onRegenerateWithContext);
                  
                  if (userContext.trim() && onRegenerateWithContext) {
                    console.log('%c🚀 [CBAComprehensiveDisplay] Starting regeneration...', 'color: #F59E0B; font-weight: bold');
                    setIsRegenerating(true);
                    
                    try {
                      await onRegenerateWithContext(userContext);
                      console.log('%c✅ [CBAComprehensiveDisplay] Regeneration complete', 'color: #10B981; font-weight: bold');
                    } catch (error) {
                      console.error('%c❌ [CBAComprehensiveDisplay] Regeneration failed:', 'color: #EF4444; font-weight: bold', error);
                    }
                    
                    setIsRegenerating(false);
                    setShowContextInput(false);
                    setUserContext('');
                  } else {
                    console.warn('%c⚠️ [CBAComprehensiveDisplay] Cannot regenerate - missing context or callback', 'color: #F59E0B');
                  }
                }}
                disabled={!userContext.trim() || isRegenerating}
                style={{
                  background: userContext.trim() && !isRegenerating
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#e5e7eb',
                  color: userContext.trim() && !isRegenerating ? 'white' : '#9ca3af',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: userContext.trim() && !isRegenerating ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                {isRegenerating ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <RefreshCw style={{ width: '14px', height: '14px' }} />
                    </motion.div>
                    Regenerating...
                  </>
                ) : (
                  <>
                    <Send style={{ width: '14px', height: '14px' }} />
                    Regenerate Analysis
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Executive Summary Section */}
      {cbaData.executive_summary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          style={{
            background: 'white',
            borderRadius: '12px',
            marginBottom: '24px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div
            onClick={() => toggleSection('executiveSummary')}
            style={{
              padding: '20px',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText style={{ width: '24px', height: '24px', color: 'white' }} />
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'white', margin: 0 }}>
                Executive Summary
              </h2>
            </div>
            {expandedSections.executiveSummary ? (
              <ChevronDown style={{ width: '24px', height: '24px', color: 'white' }} />
            ) : (
              <ChevronRight style={{ width: '24px', height: '24px', color: 'white' }} />
            )}
          </div>
          
          <AnimatePresence>
            {expandedSections.executiveSummary && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ padding: '24px', lineHeight: '1.8', color: '#374151' }}>
                  {cbaData.executive_summary && (
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {cbaData.executive_summary.split('\n').map((paragraph, idx) => (
                        <div key={idx} style={{ 
                          marginBottom: paragraph.trim() ? '16px' : '8px',
                          fontSize: paragraph.startsWith('•') ? '14px' : '15px',
                          paddingLeft: paragraph.startsWith('•') ? '20px' : '0'
                        }}>
                          {paragraph}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Costs Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        style={{
          background: 'white',
          borderRadius: '12px',
          marginBottom: '24px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div
          onClick={() => toggleSection('costs')}
          style={{
            padding: '20px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <TrendingDown style={{ width: '24px', height: '24px', color: '#dc2626' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626', margin: 0 }}>
              Detailed Cost Analysis
            </h2>
            <span style={{
              background: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#dc2626'
            }}>
              {cbaData.costs?.length || 0} items
            </span>
          </div>
          {expandedSections.costs ? (
            <ChevronDown style={{ width: '24px', height: '24px', color: '#dc2626' }} />
          ) : (
            <ChevronRight style={{ width: '24px', height: '24px', color: '#dc2626' }} />
          )}
        </div>
        
        <AnimatePresence>
          {expandedSections.costs && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <div style={{ padding: '20px' }}>
                {Object.entries(costsByCategory).map(([category, items]) => (
                  <div key={category} style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {category}
                    </h3>
                    {items.map((item, index) => {
                      const itemId = `cost-${category}-${index}`;
                      const isExpanded = expandedItems[itemId];
                      
                      return (
                        <div
                          key={index}
                          style={{
                            background: '#f9fafb',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer'
                          }}
                          onClick={() => toggleItem(itemId)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isExpanded ? (
                                  <ChevronDown style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                ) : (
                                  <ChevronRight style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                )}
                                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                                  {item.title}
                                </h4>
                              </div>
                              <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginLeft: '24px' }}>
                                {item.description}
                              </p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626', margin: 0 }}>
                                {formatCurrency(item.amount)}
                              </p>
                              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                {item.frequency}
                              </p>
                            </div>
                          </div>
                          
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                style={{
                                  marginTop: '16px',
                                  paddingTop: '16px',
                                  borderTop: '1px solid #e5e7eb'
                                }}
                              >
                                {/* Methodology */}
                                {item.methodology && (
                                  <div style={{ marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <Calculator style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                                        CALCULATION METHODOLOGY
                                      </span>
                                    </div>
                                    <div style={{
                                      background: 'white',
                                      padding: '12px',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      lineHeight: '1.6',
                                      color: '#374151',
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                      {item.methodology}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Citations */}
                                {item.citations && item.citations.length > 0 && (
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <BookOpen style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                                        DATA SOURCES & CITATIONS
                                      </span>
                                    </div>
                                    <div style={{
                                      background: 'white',
                                      padding: '12px',
                                      borderRadius: '6px'
                                    }}>
                                      {item.citations.map((citation, idx) => (
                                        <div key={idx} style={{
                                          fontSize: '12px',
                                          color: '#4b5563',
                                          marginBottom: idx < item.citations.length - 1 ? '8px' : 0,
                                          paddingLeft: '16px',
                                          position: 'relative'
                                        }}>
                                          <span style={{
                                            position: 'absolute',
                                            left: 0,
                                            color: '#9ca3af'
                                          }}>•</span>
                                          {citation}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Benefits Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45 }}
        style={{
          background: 'white',
          borderRadius: '12px',
          marginBottom: '24px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div
          onClick={() => toggleSection('benefits')}
          style={{
            padding: '20px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #bbf7d0 0%, #86efac 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <TrendingUp style={{ width: '24px', height: '24px', color: '#16a34a' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#16a34a', margin: 0 }}>
              Detailed Benefit Analysis
            </h2>
            <span style={{
              background: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#16a34a'
            }}>
              {cbaData.benefits?.length || 0} items
            </span>
          </div>
          {expandedSections.benefits ? (
            <ChevronDown style={{ width: '24px', height: '24px', color: '#16a34a' }} />
          ) : (
            <ChevronRight style={{ width: '24px', height: '24px', color: '#16a34a' }} />
          )}
        </div>
        
        <AnimatePresence>
          {expandedSections.benefits && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <div style={{ padding: '20px' }}>
                {Object.entries(benefitsByCategory).map(([category, items]) => (
                  <div key={category} style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {category}
                    </h3>
                    {items.map((item, index) => {
                      const itemId = `benefit-${category}-${index}`;
                      const isExpanded = expandedItems[itemId];
                      
                      return (
                        <div
                          key={index}
                          style={{
                            background: '#f9fafb',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer'
                          }}
                          onClick={() => toggleItem(itemId)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isExpanded ? (
                                  <ChevronDown style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                ) : (
                                  <ChevronRight style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                                )}
                                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                                  {item.title}
                                </h4>
                              </div>
                              <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginLeft: '24px' }}>
                                {item.description}
                              </p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a', margin: 0 }}>
                                {formatCurrency(item.amount)}
                              </p>
                              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                {item.frequency}
                              </p>
                            </div>
                          </div>
                          
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                style={{
                                  marginTop: '16px',
                                  paddingTop: '16px',
                                  borderTop: '1px solid #e5e7eb'
                                }}
                              >
                                {/* Methodology */}
                                {item.methodology && (
                                  <div style={{ marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <Calculator style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                                        CALCULATION METHODOLOGY
                                      </span>
                                    </div>
                                    <div style={{
                                      background: 'white',
                                      padding: '12px',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      lineHeight: '1.6',
                                      color: '#374151',
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                      {item.methodology}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Citations */}
                                {item.citations && item.citations.length > 0 && (
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                      <BookOpen style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                                        DATA SOURCES & CITATIONS
                                      </span>
                                    </div>
                                    <div style={{
                                      background: 'white',
                                      padding: '12px',
                                      borderRadius: '6px'
                                    }}>
                                      {item.citations.map((citation, idx) => (
                                        <div key={idx} style={{
                                          fontSize: '12px',
                                          color: '#4b5563',
                                          marginBottom: idx < item.citations.length - 1 ? '8px' : 0,
                                          paddingLeft: '16px',
                                          position: 'relative'
                                        }}>
                                          <span style={{
                                            position: 'absolute',
                                            left: 0,
                                            color: '#9ca3af'
                                          }}>•</span>
                                          {citation}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Qualitative Factors */}
      {cbaData.qualitative_factors && cbaData.qualitative_factors.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{
            background: 'white',
            borderRadius: '12px',
            marginBottom: '24px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div
            onClick={() => toggleSection('qualitative')}
            style={{
              padding: '20px',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AlertCircle style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#92400e', margin: 0 }}>
                Qualitative Factors
              </h2>
            </div>
            {expandedSections.qualitative ? (
              <ChevronDown style={{ width: '24px', height: '24px', color: '#92400e' }} />
            ) : (
              <ChevronRight style={{ width: '24px', height: '24px', color: '#92400e' }} />
            )}
          </div>
          
          <AnimatePresence>
            {expandedSections.qualitative && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ padding: '20px' }}>
                  {cbaData.qualitative_factors.map((factor, index) => {
                    // Check if factor is a string or object
                    const isString = typeof factor === 'string';
                    const isCostFactor = isString && factor.toLowerCase().includes('cost factor');
                    
                    return (
                      <div key={index} style={{
                        background: isCostFactor ? '#fef2f2' : '#f0fdf4',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        border: isCostFactor ? '1px solid #fecaca' : '1px solid #bbf7d0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: isCostFactor ? '#dc2626' : '#10b981',
                          marginTop: '5px',
                          flexShrink: 0
                        }} />
                        <div style={{ flex: 1 }}>
                          {isString ? (
                            <p style={{ 
                              fontSize: '14px', 
                              color: '#374151', 
                              lineHeight: '1.6',
                              margin: 0
                            }}>
                              {factor.replace(/^(Cost Factor:|Benefit Factor:)\s*/i, '')}
                            </p>
                          ) : (
                            <>
                              <h4 style={{ 
                                fontSize: '15px', 
                                fontWeight: '600', 
                                color: isCostFactor ? '#991b1b' : '#14532d', 
                                marginBottom: '8px' 
                              }}>
                                {factor.title}
                              </h4>
                              <p style={{ 
                                fontSize: '13px', 
                                color: '#4b5563', 
                                lineHeight: '1.6' 
                              }}>
                                {factor.description}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Methodology & Assumptions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
        style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div
          onClick={() => toggleSection('methodology')}
          style={{
            padding: '20px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Database style={{ width: '24px', height: '24px', color: '#6b7280' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', margin: 0 }}>
              Methodology, Assumptions & Data Sources
            </h2>
          </div>
          {expandedSections.methodology ? (
            <ChevronDown style={{ width: '24px', height: '24px', color: '#6b7280' }} />
          ) : (
            <ChevronRight style={{ width: '24px', height: '24px', color: '#6b7280' }} />
          )}
        </div>
        
        <AnimatePresence>
          {expandedSections.methodology && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <div style={{ padding: '24px' }}>
                {/* Assumptions */}
                {cbaData.assumptions && cbaData.assumptions.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      KEY ASSUMPTIONS
                    </h3>
                    <div style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      {cbaData.assumptions.map((assumption, idx) => (
                        <div key={idx} style={{
                          fontSize: '13px',
                          color: '#374151',
                          marginBottom: idx < cbaData.assumptions.length - 1 ? '8px' : 0,
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            color: '#9ca3af'
                          }}>{idx + 1}.</span>
                          {assumption}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Data Sources */}
                {cbaData.data_sources && cbaData.data_sources.length > 0 && (
                  <div>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      PRIMARY DATA SOURCES
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {cbaData.data_sources.map((source, idx) => (
                        <div key={idx} style={{
                          background: '#f9fafb',
                          borderRadius: '8px',
                          padding: '12px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div>
                            <p style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                              {source.name}
                            </p>
                            <p style={{ fontSize: '11px', color: '#6b7280' }}>
                              {source.type} • {source.year}
                            </p>
                            {source.url && source.url.startsWith('http') && (
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '10px',
                                  color: '#2563eb',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  marginTop: '4px'
                                }}
                              >
                                View source <ExternalLink style={{ width: '10px', height: '10px' }} />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default CBAComprehensiveDisplay;